import { CommonModule } from '@angular/common';
import { ErreurApi, normaliserErreurHttp } from '../../Services/api-error';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { EtatService } from '../../Services/etat';
import { RentabilitePerformanceService } from '../../Services/rentabilite-performance.service';
import { RentabilitePerformanceApi } from '../../Services/rentabilite-performance.types';

type ColonneTri = keyof Pick<LignePerformance, 'centrale' | 'batiment' | 'irradiation' | 'production' | 'productionTheorique' | 'productible' | 'pr' | 'performance' | 'gain' | 'rentabilite' | 'puissance' | 'perte' | 'disponibilite'>;
type DirectionTri = 'asc' | 'desc';

interface LignePerformance {
  id: number;
  idInstallation: number;
  centrale: string;
  batiment: string;
  irradiation: number | null;
  production: number | null;
  productionTheorique: number | null;
  ecart?: number | null;
  productible: number | null;
  pr: number | null;
  performance: number | null;
  gain: number | null;
  rentabilite: number | null;
  puissance: number | null;
  perte: number | null;
  disponibilite: number | null;
}

@Component({
  selector: 'app-rentabilite-performance',
  imports: [CommonModule, FormsModule, MatButtonModule, MatSnackBarModule],
  templateUrl: './rentabilite-performance.html',
  styleUrl: './rentabilite-performance.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RentabilitePerformance {
  private readonly etat = inject(EtatService);
  private readonly service = inject(RentabilitePerformanceService);
  private readonly notifications = inject(MatSnackBar);

  readonly dateDebut = signal('');
  readonly dateFin = signal('');
  readonly installation = signal<number | null>(null);
  readonly recherche = signal('');
  readonly regrouper = signal(false);
  readonly taillePage = signal(10);
  readonly page = signal(1);
  readonly tri = signal<ColonneTri>('centrale');
  readonly direction = signal<DirectionTri>('asc');
  readonly partageEffectue = signal(false);
  readonly chargement = signal(false);
  readonly exportEnCours = signal(false);
  readonly erreur = signal<string | null>(null);
  readonly lignes = signal<LignePerformance[]>([]);
  readonly parcSelectionne = this.etat.parcSelectionne;
  private requeteCourante = 0;
  private destroyRef = inject(DestroyRef);

  readonly centrales = computed(() => this.etat.installations()
    .map(({ id, nom }) => ({ id, nom }))
    .sort((a, b) => a.nom.localeCompare(b.nom, 'fr')));

  readonly lignesFiltrees = computed(() => {
    const terme = this.recherche().trim().toLocaleLowerCase('fr');
    let lignes = this.lignes().filter((ligne) => !terme || `${ligne.centrale} ${ligne.batiment}`.toLocaleLowerCase('fr').includes(terme));
    if (this.regrouper()) lignes = this.agregerParCentrale(lignes);

    const colonne = this.tri();
    const facteur = this.direction() === 'asc' ? 1 : -1;
    return [...lignes].sort((a, b) => {
      const valeurA = a[colonne];
      const valeurB = b[colonne];
      if (valeurA == null && valeurB == null) return 0;
      if (valeurA == null) return 1;
      if (valeurB == null) return -1;
      return (typeof valeurA === 'string' ? valeurA.localeCompare(String(valeurB), 'fr') : valeurA - Number(valeurB)) * facteur;
    });
  });

  readonly totalPages = computed(() => Math.max(1, Math.ceil(this.lignesFiltrees().length / this.taillePage())));
  readonly lignesPage = computed(() => {
    const debut = (Math.min(this.page(), this.totalPages()) - 1) * this.taillePage();
    return this.lignesFiltrees().slice(debut, debut + this.taillePage());
  });
  readonly debutAffichage = computed(() => this.lignesFiltrees().length ? (Math.min(this.page(), this.totalPages()) - 1) * this.taillePage() + 1 : 0);
  readonly finAffichage = computed(() => Math.min(this.debutAffichage() + this.taillePage() - 1, this.lignesFiltrees().length));

  readonly synthese = computed(() => {
    const lignes = this.lignesFiltrees();
    const somme = (champ: 'production' | 'gain' | 'puissance') => lignes.reduce((total, ligne) => total + (ligne[champ] ?? 0), 0);
    const valeursPr = lignes.map((ligne) => ligne.pr).filter((valeur): valeur is number => valeur !== null);
    const moyennePr = valeursPr.length ? valeursPr.reduce((total, valeur) => total + valeur, 0) / valeursPr.length : null;
    return { productionMWh: somme('production') / 1000, gain: somme('gain'), puissance: somme('puissance'), pr: moyennePr };
  });

  constructor() {
    effect(() => {
      const parcId = this.parcSelectionne()?.id;
      ++this.requeteCourante;
      this.lignes.set([]);
      this.erreur.set(null);
      this.chargement.set(false);
      if (parcId != null) untracked(() => {
        void this.etat.obtenirInstallationsParcCourant().catch(() => undefined);
        void this.charger(parcId);
      });
    });
    if (!this.etat.parcs().length) void this.etat.chargerParcs();
  }

  async appliquerFiltres(): Promise<void> {
    const parcId = this.parcSelectionne()?.id;
    if (parcId != null) await this.charger(parcId);
  }

  async exporterExcel(): Promise<void> {
    if (this.exportEnCours()) return;
    const parcId = this.parcSelectionne()?.id;
    if (parcId == null) {
      this.notifierErreurExport();
      return;
    }

    this.exportEnCours.set(true);
    try {
      const reponse = await this.service.exporterRentabiliteParc(parcId, {
        debut: this.dateDebut() || undefined,
        fin: this.dateFin() || undefined,
        installation: this.installation() ?? undefined,
      });
      if (!reponse.body) throw new Error('Le fichier exporté est vide.');

      const nom = this.extraireNomFichier(reponse.headers.get('Content-Disposition'))
        ?? this.nomFichierSecours(parcId);
      const url = URL.createObjectURL(reponse.body);
      try {
        const lien = document.createElement('a');
        lien.href = url;
        lien.download = nom;
        lien.style.display = 'none';
        document.body.appendChild(lien);
        lien.click();
        lien.remove();
      } finally {
        URL.revokeObjectURL(url);
      }
    } catch {
      this.notifierErreurExport();
    } finally {
      this.exportEnCours.set(false);
    }
  }

  changerTri(colonne: ColonneTri): void {
    if (this.tri() === colonne) this.direction.update((direction) => direction === 'asc' ? 'desc' : 'asc');
    else { this.tri.set(colonne); this.direction.set('asc'); }
    this.page.set(1);
  }

  changerTaille(taille: number): void { this.taillePage.set(Number(taille)); this.page.set(1); }
  pagePrecedente(): void { this.page.update((page) => Math.max(1, page - 1)); }
  pageSuivante(): void { this.page.update((page) => Math.min(this.totalPages(), page + 1)); }

  async partager(): Promise<void> {
    const texte = `Performance du ${this.dateDebut()} au ${this.dateFin()}`;
    if (typeof navigator !== 'undefined' && navigator.share) await navigator.share({ title: 'Rentabilité / Performance', text: texte }).catch(() => undefined);
    else if (typeof navigator !== 'undefined' && navigator.clipboard) await navigator.clipboard.writeText(texte).catch(() => undefined);
    this.partageEffectue.set(true);
    setTimeout(() => this.partageEffectue.set(false), 1800);
  }

  indicateurTri(colonne: ColonneTri): string {
    if (this.tri() !== colonne) return 'unfold_more';
    return this.direction() === 'asc' ? 'arrow_upward' : 'arrow_downward';
  }

  private async charger(parcId: number): Promise<void> {
    const numeroRequete = ++this.requeteCourante;
    const estValide = () => numeroRequete === this.requeteCourante && !this.destroyRef.destroyed && parcId === this.parcSelectionne()?.id;
    this.chargement.set(true);
    this.erreur.set(null);
    this.page.set(1);
    try {
      const reponse = await this.service.getRentabiliteParc(parcId, {
        debut: this.dateDebut() || undefined,
        fin: this.dateFin() || undefined,
        installation: this.installation() ?? undefined,
      });
      if (!estValide()) return;
      if (!reponse) throw new Error('La réponse reçue est invalide.');
      this.dateDebut.set(reponse.debut);
      this.dateFin.set(reponse.fin);
      this.lignes.set(reponse.performances.map((ligne) => this.convertirLigne(ligne)));
    } catch (erreur: unknown) {
      if (!estValide()) return;
      this.lignes.set([]);
      this.erreur.set(this.messageErreur(erreur));
    } finally {
      if (estValide()) this.chargement.set(false);
    }
  }

  private convertirLigne(ligne: RentabilitePerformanceApi): LignePerformance {
    return {
      id: ligne.id_batiment,
      idInstallation: ligne.id_installation,
      centrale: ligne.centrale,
      batiment: ligne.batiment ?? '—',
      irradiation: ligne.irradiation == null ? null : ligne.irradiation / 1000,
      production: ligne.production,
      productionTheorique: ligne.production_theorique != 0 ? ligne.production_theorique : null,
      ecart: (ligne.production !== null && ligne.production_theorique !== null && ligne.production_theorique > 0) ? ((ligne.production - ligne.production_theorique) / ligne.production_theorique) * 100 : null,
      productible: ligne.productible == null ? null : ligne.productible / 1000,
      pr: ligne.pr == null ? null : ligne.pr * 100,
      performance: ligne.performance,
      gain: ligne.gain,
      rentabilite: ligne.rentabilite,
      puissance: ligne.puissance_crete,
      perte: ligne.perte = Math.max(0, (ligne.production_theorique ?? 0) - (ligne.production ?? 0)),
      disponibilite: ligne.disponibilite,
    };
  }

  private messageErreur(erreur: unknown): string {
    erreur = normaliserErreurHttp(erreur);
    if (erreur instanceof ErreurApi) {
      const details = erreur.details;
      if (typeof details === 'string' && details.trim()) return details;
      if (erreur.status === 403) return "Vous n’êtes pas autorisé à consulter ce parc.";
      if (erreur.status === 404) return 'Le parc demandé est introuvable.';
      if (erreur.status === 422) return 'La période ou la centrale sélectionnée est invalide.';
    }
    return erreur instanceof Error ? erreur.message : 'Impossible de charger les performances du parc.';
  }

  private extraireNomFichier(contentDisposition: string | null): string | null {
    if (!contentDisposition) return null;
    const utf8 = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i)?.[1];
    if (utf8) {
      try { return decodeURIComponent(utf8.trim().replace(/^"|"$/g, '')); }
      catch { return utf8.trim().replace(/^"|"$/g, ''); }
    }
    return contentDisposition.match(/filename\s*=\s*"([^"]+)"/i)?.[1]
      ?? contentDisposition.match(/filename\s*=\s*([^;]+)/i)?.[1]?.trim()
      ?? null;
  }

  private nomFichierSecours(parcId: number): string {
    const debut = this.dateDebut() || 'debut';
    const fin = this.dateFin() || 'fin';
    return `rentabilite_parc_${parcId}_${debut}_${fin}.xlsx`;
  }

  private notifierErreurExport(): void {
    this.notifications.open("L’export Excel a échoué. Veuillez réessayer.", 'Fermer', {
      duration: 5000,
      panelClass: ['error-snackbar'],
    });
  }

  private agregerParCentrale(lignes: LignePerformance[]): LignePerformance[] {
    const groupes = new Map<number, LignePerformance[]>();
    for (const ligne of lignes) groupes.set(ligne.idInstallation, [...(groupes.get(ligne.idInstallation) ?? []), ligne]);
    const sommeNullable = (groupe: LignePerformance[], champ: keyof Pick<LignePerformance, 'production' | 'productionTheorique' | 'productible' | 'gain' | 'puissance' | 'perte'>) => {
      const valeurs = groupe.map((ligne) => ligne[champ]).filter((valeur): valeur is number => valeur !== null);
      return valeurs.length ? valeurs.reduce((total, valeur) => total + valeur, 0) : null;
    };
    const moyenneNullable = (groupe: LignePerformance[], champ: keyof Pick<LignePerformance, 'irradiation' | 'pr' | 'performance' | 'rentabilite' | 'disponibilite'>) => {
      const valeurs = groupe.map((ligne) => ligne[champ]).filter((valeur): valeur is number => valeur !== null);
      return valeurs.length ? valeurs.reduce((total, valeur) => total + valeur, 0) / valeurs.length : null;
    };
    return [...groupes.entries()].map(([idInstallation, groupe]) => {
      const production = sommeNullable(groupe, 'production');
      const productionTheorique = sommeNullable(groupe, 'productionTheorique');
      return {
        id: -idInstallation, idInstallation, centrale: groupe[0].centrale,
        batiment: groupe.length > 1 ? `${groupe.length} bâtiments` : groupe[0].batiment,
        irradiation: moyenneNullable(groupe, 'irradiation'), production,
        productionTheorique,
        ecart: production !== null && productionTheorique !== null && productionTheorique > 0 ? ((production - productionTheorique) / productionTheorique) * 100 : null,
        productible: sommeNullable(groupe, 'productible'),
        pr: moyenneNullable(groupe, 'pr'), performance: moyenneNullable(groupe, 'performance'), gain: sommeNullable(groupe, 'gain'),
        rentabilite: moyenneNullable(groupe, 'rentabilite'), puissance: sommeNullable(groupe, 'puissance'),
        perte: sommeNullable(groupe, 'perte'), disponibilite: moyenneNullable(groupe, 'disponibilite'),
      };
    });
  }
}
